import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import type { Notice, NoticeDisplayType } from '../../types/notice.types';

/**
 * 실시간 공지사항 구독 훅
 * onSnapshot으로 Firestore notices 컬렉션을 실시간 구독하고
 * 클라이언트에서 활성/시간 조건 필터링 수행
 */
export function useNotices() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const ref = collection(db, 'notices');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setLoading(false);
        const items: Notice[] = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Notice[];
        console.log('[useNotices] Firestore에서 가져온 공지:', items.length, '개', items);
        setNotices(items);
      },
      (err) => {
        setLoading(false);
        setError(err?.message || '공지사항 구독 실패');
        console.error('[useNotices] 구독 에러:', err);
      }
    );
    return () => unsub();
  }, []);

  /** 현재 시간 기준 활성 공지만 필터 */
  const activeNotices = useMemo(() => {
    const now = Date.now();
    return notices
      .filter((n) => {
        if (!n.isActive) return false;

        // startAt 체크
        if (n.startAt) {
          const startTime = (n.startAt as any)?.toMillis
            ? (n.startAt as any).toMillis()
            : new Date(n.startAt as any).getTime();
          if (now < startTime) return false;
        }

        // endAt 체크
        if (n.endAt) {
          const endTime = (n.endAt as any)?.toMillis
            ? (n.endAt as any).toMillis()
            : new Date(n.endAt as any).getTime();
          if (now > endTime) return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
        return (a.order ?? 0) - (b.order ?? 0);
      });
  }, [notices]);

  /** displayType별 필터 유틸 */
  const getByType = (type: NoticeDisplayType) =>
    activeNotices.filter((n) => n.displayType === type);

  const bannerNotices = useMemo(() => getByType('banner'), [activeNotices]);
  const cardNotices = useMemo(() => getByType('card'), [activeNotices]);
  const tickerNotices = useMemo(() => getByType('ticker'), [activeNotices]);

  console.log('[useNotices] 전체:', notices.length, '활성:', activeNotices.length, '배너:', bannerNotices.length, '카드:', cardNotices.length, '티커:', tickerNotices.length);

  return {
    notices,
    activeNotices,
    bannerNotices,
    cardNotices,
    tickerNotices,
    loading,
    error,
  };
}

/**
 * 배너 해제 localStorage 관리
 */
const BANNER_DISMISS_PREFIX = 'notice_banner_dismissed_';

export function isBannerDismissedToday(noticeId: string): boolean {
  const key = BANNER_DISMISS_PREFIX + noticeId;
  const dismissedUntil = localStorage.getItem(key);
  if (!dismissedUntil) return false;
  return parseInt(dismissedUntil, 10) > Date.now();
}

export function setBannerDismissedToday(noticeId: string): void {
  const key = BANNER_DISMISS_PREFIX + noticeId;
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  localStorage.setItem(key, midnight.getTime().toString());
}
